import { useState, useMemo } from "react";
import { NavLink, useParams, useLocation } from "react-router-dom";
import { ArrowLeftRight, Plus, ClipboardList, Users, BarChart3, RefreshCw, SlidersHorizontal, Eye, EyeOff } from 'lucide-react';
import { useClasseContext } from "../../context/ClasseContext";
import { useUser } from "../../context/UserContext";
import { formatLettresDate } from "../../utils/ClasseEvaluations";
import { getAccessType } from "../../utils/permissions";
import { ETAT_CHANGER_DE_CLASSE } from "../../hooks/ui/useArriveeChangerDeClasse";
import { estDemiGroupeVide } from "../../utils/groupesDeBesoin";
import {
    navItemClasses,
    subNavItemClasses,
    navItemStyle,
    ActiveBar,
    iconClasses,
} from './sidebarStyles';

function SideBarProf({ periode, anneescolaire }) {
    const { classeNom, groupNom, classeDetail, ActivePeriode, setActivePeriode } = useClasseContext();
    const { resultatsFilter, setResultatsFilter, etablissementData, user } = useUser();
    const isGAR = true;
    const periods_label = ["P1", "P2", "P3", "P4", "P5"];
    const [selectedPeriod, setSelectedPeriod] = useState(null);
    const params = useParams();
    const idclasse = params.idclasse;

    const accessType = useMemo(() => {
        if (!idclasse || !user || !etablissementData) return 'none';
        return getAccessType(parseInt(idclasse, 10), user, etablissementData);
    }, [idclasse, user, etablissementData]);

    const location = useLocation();

    // Sections à état actif étendu : "Créer une évaluation" reste allumée sur les
    // écrans de composition (autre page, même menu), "Pilotage" sur ses cinq pages.
    const sectionActive = useMemo(() => {
        const path = location.pathname;
        const creationRoutes = [`creer/${idclasse}`, `qcm/${idclasse}`, `eval-bilan/${idclasse}`, `evals/${idclasse}/add`, `add-common-eval`];
        if (creationRoutes.some(r => path.includes(r))) return 'creation';
        const pilotageRoutes = [`detail/${idclasse}`, `progression/${idclasse}`, `eval-nat/${idclasse}`, `groupes/${idclasse}`, `savoirs/${idclasse}`];
        if (pilotageRoutes.some(r => path.includes(r))) return 'pilotage';
        return null;
    }, [location.pathname, idclasse]);

    const handleClickPeriod = (period) => {
        if (selectedPeriod === period) {
            setSelectedPeriod(null);
            setActivePeriode(anneescolaire);
        } else {
            setSelectedPeriod(period);
            setActivePeriode(period);
        }
    };

    const nbElevesNonSynchro = useMemo(() => {
        if (!classeDetail || !classeDetail.eleves_gar_details) return 0;
        // Exclure les comptes ENT DÉJÀ liés à une fiche synchronisée (fiche.eleve_gar === gar_details.id) :
        // leur flag back `non_synchronise` peut rester à true, mais ils sont bel et bien liés
        // → sans ce garde la pastille reste affichée alors que l'élève est synchronisé
        // (même racine que le doublon corrigé dans SynchronisationGAR.jsx).
        const garIdsLies = new Set(
            (classeDetail.eleves || []).filter(e => e.synchronise_gar).map(e => e.eleve_gar),
        );
        return classeDetail.eleves_gar_details.filter(
            g => g.non_synchronise === true && !garIdsLies.has(g.id),
        ).length;
    }, [classeDetail]);

    const hasEleves = !!(classeDetail.eleves && classeDetail.eleves.length !== 0);
    // Demi-groupe encore vide (décision prof 2026-09-30) : on le compose d'abord,
    // le menu se réduit à « Changer de classe » et « Élèves », la page où l'on
    // coche ses élèves. Classes et groupes de besoin : menu inchangé.
    const aComposer = Boolean(groupNom && !classeNom) && estDemiGroupeVide(classeDetail);
    const isPilotageActive = sectionActive === 'pilotage';
    const isCreationActive = sectionActive === 'creation';

    // Bloc info classe + filtres période : masqué par défaut (allège la sidebar),
    // affichable depuis Réglages. Préférence mémorisée (perdue au logout : localStorage.clear()).
    const [showFiltres, setShowFiltres] = useState(() => localStorage.getItem('sidebarFiltresVisible') === '1');
    const toggleFiltres = () => {
        const next = !showFiltres;
        localStorage.setItem('sidebarFiltresVisible', next ? '1' : '0');
        setShowFiltres(next);
    };
    // Un filtre est en cours alors que le bloc est masqué → pastille sur l'entrée Réglages.
    const filtreActif = selectedPeriod !== null || (!!groupNom && resultatsFilter === 'groupe');

    return (
        <>
            <div className="h-2" />

            {/* Retour à la sélection de classe — SideBarProf ne s'affiche qu'en contexte
                classe : ce lien sort toujours de la classe courante. La classe se vide
                à l'ARRIVÉE sur l'accueil (useArriveeChangerDeClasse dans Sidebar), plus
                au clic : une saisie de notes non sauvegardée peut retenir la navigation. */}
            <div className="flex-1 px-3 py-2">
                <NavLink to="/home/" state={ETAT_CHANGER_DE_CLASSE} end className={navItemClasses} style={navItemStyle}>
                    {({ isActive }) => (
                        <>
                            {isActive && <ActiveBar />}
                            <ArrowLeftRight size={18} className={iconClasses(isActive)} />
                            <span className="text-[14px] font-medium leading-tight flex-1">Changer de classe</span>
                        </>
                    )}
                </NavLink>
            </div>

            {/* Créer une évaluation — CTA du parcours central */}
            {!aComposer ? (
            <div className="flex-1 px-3 py-2">
                <NavLink
                    to={`creer/${idclasse}`}
                    className={() =>
                        isCreationActive
                            ? navItemClasses({ isActive: true })
                            : 'group relative w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-left transition-all duration-200 text-white bg-white/10 border border-cyan-300/40 hover:bg-white/20'
                    }
                    style={() => navItemStyle({ isActive: isCreationActive })}
                >
                    {isCreationActive && <ActiveBar />}
                    <Plus size={18} className={isCreationActive ? iconClasses(true) : 'shrink-0 text-cyan-200'} />
                    <span className="text-[14px] font-medium leading-tight flex-1">Créer une évaluation</span>
                </NavLink>
            </div>
            ) : null}

            {/* Évaluations */}
            {(hasEleves && accessType === 'full') && (
                <div className="flex-1 px-3 py-2">
                    <NavLink to={`evaluations/${idclasse}`} end className={navItemClasses} style={navItemStyle}>
                        {({ isActive }) => (
                            <>
                                {isActive && <ActiveBar />}
                                <ClipboardList size={18} className={iconClasses(isActive)} />
                                <span className="text-[14px] font-medium leading-tight flex-1">Évaluations</span>
                            </>
                        )}
                    </NavLink>
                </div>
            )}

            {/* Élèves */}
            {(hasEleves || aComposer) && (
                <div className="flex-1 px-3 py-2">
                    <NavLink to={`eleves/${idclasse}`} className={navItemClasses} style={navItemStyle}>
                        {({ isActive }) => (
                            <>
                                {isActive && <ActiveBar />}
                                <Users size={18} className={iconClasses(isActive)} />
                                <span className="text-[14px] font-medium leading-tight flex-1">Élèves</span>
                            </>
                        )}
                    </NavLink>
                </div>
            )}

            {/* Pilotage — tableau de bord + onglets (progression, éval nat, groupes, savoirs) */}
            {!aComposer ? (
            <div className="flex-1 px-3 py-2">
                <NavLink
                    to={`detail/${idclasse}`}
                    className={() => navItemClasses({ isActive: isPilotageActive })}
                    style={() => navItemStyle({ isActive: isPilotageActive })}
                >
                    {isPilotageActive && <ActiveBar />}
                    <BarChart3 size={18} className={iconClasses(isPilotageActive)} />
                    <span className="text-[14px] font-medium leading-tight flex-1">Pilotage</span>
                </NavLink>
            </div>
            ) : null}

            {/* Réglages */}
            {!aComposer ? (
            <>
            <div className="px-6 pt-3 pb-1">
                <span className="text-[11px] font-semibold uppercase tracking-wider text-blue-200/60">Réglages</span>
            </div>
            <ul>
                {(isGAR && accessType === 'full') && (
                    <li className="ml-6 pl-3 border-l border-blue-300/30 py-1.5 list-none">
                        <NavLink to={`synchronisation/${idclasse}`} className={subNavItemClasses} style={navItemStyle}>
                            {({ isActive }) => (
                                <>
                                    {isActive && <ActiveBar />}
                                    <RefreshCw size={16} className={iconClasses(isActive)} />
                                    <span className="text-[13px] font-medium leading-tight flex-1">Synchronisation GAR</span>
                                    {nbElevesNonSynchro > 0 && (
                                        <span className="ml-1 bg-orange-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full">
                                            {nbElevesNonSynchro}
                                        </span>
                                    )}
                                </>
                            )}
                        </NavLink>
                    </li>
                )}
                <li className="ml-6 pl-3 border-l border-blue-300/30 py-1.5 list-none">
                    <button onClick={toggleFiltres} className={subNavItemClasses({ isActive: false })}>
                        <SlidersHorizontal size={16} className={iconClasses(false)} />
                        <span className="text-[13px] font-medium leading-tight flex-1 text-left">Filtres de période</span>
                        {(filtreActif && !showFiltres) && (
                            <span className="w-2 h-2 rounded-full bg-orange-400 shrink-0" title="Un filtre est actif" />
                        )}
                        {showFiltres
                            ? <EyeOff size={14} className="text-slate-400 group-hover:text-white shrink-0" />
                            : <Eye size={14} className="text-slate-400 group-hover:text-white shrink-0" />}
                    </button>
                </li>
            </ul>
            </>
            ) : null}

            {/* Bloc info classe + filtres période — affiché à la demande via Réglages */}
            {(showFiltres && !aComposer) && (
            <div className="p-4 xl:p-2 3xl:p-4 space-y-4 xl:space-y-2 3xl:space-y-4">
                <div
                    style={{
                        background: `linear-gradient(to right, rgb(30 41 59 / 0.6), rgb(51 65 85 / 0.4), rgb(71 85 105 / 0.3))`
                    }}
                    className="rounded-lg p-4 xl:p-2 3xl:p-4 space-y-3 xl:space-y-1 3xl:space-y-3 shadow-md"
                >
                    <div>
                        <div className="text-white font-bold text-sm xl:text-xs 3xl:text-sm mb-1">{classeNom ? `Classe - ${classeNom}` : `Groupe - ${groupNom}`}</div>
                        <div className="text-orange-300 text-sm xl:text-xs 3xl:text-sm mb-2 xl:mb-1 3xl:mb-2">Filtrage actif - <span className="text-orange-400">{ActivePeriode && ActivePeriode.type_periode + " " + (ActivePeriode.numero ? ActivePeriode.numero : "")}</span></div>
                        <div className="text-blue-200 text-xs mb-1">Date de début : {ActivePeriode && formatLettresDate(ActivePeriode.date_debut)}</div>
                        <div className="text-blue-200 text-xs">Date de fin : {ActivePeriode && formatLettresDate(ActivePeriode.date_fin)}</div>
                    </div>

                    {groupNom && (
                        <div>
                            <div className="text-white text-sm xl:text-xs 3xl:text-sm font-medium mb-2 xl:mb-1 3xl:mb-2">Résultats</div>
                            <div className="flex space-x-2">
                                <button
                                    onClick={() => setResultatsFilter('combinés')}
                                    className={`px-3 py-1 text-xs rounded border transition-colors duration-200 ${
                                        resultatsFilter === 'combinés'
                                            ? "bg-orange-500 text-white border-orange-400"
                                            : "bg-blue-600/60 text-white border-blue-400 hover:bg-blue-600"
                                    }`}
                                >Résultats combinés</button>
                                <button
                                    onClick={() => setResultatsFilter('groupe')}
                                    className={`px-3 py-1 text-xs rounded border transition-colors duration-200 ${
                                        resultatsFilter === 'groupe'
                                            ? "bg-orange-500 text-white border-orange-400"
                                            : "bg-blue-600/60 text-white border-blue-400 hover:bg-blue-600"
                                    }`}
                                >Résultats du groupe</button>
                            </div>
                        </div>
                    )}

                    {etablissementData?.periodes_scolaires?.length > 0 && (
                        <div>
                            <p className="text-xs xl:text-xs 3xl:text-sm">Le {etablissementData.periodes_scolaires.length > 2 ? "trimestre" : "semestre"}</p>
                            <div className="flex flex-row gap-2">
                                {etablissementData.periodes_scolaires.map((p, index) => (
                                    <div
                                        key={index}
                                        onClick={() => handleClickPeriod(p)}
                                        className={`w-10 h-10 xl:w-8 xl:h-8 3xl:w-10 3xl:h-10 flex items-center border justify-center rounded cursor-pointer transition-colors duration-200
                                            ${selectedPeriod === p
                                                ? "bg-orange-500 text-white"
                                                : "bg-blue-600/60 text-white border-blue-400 hover:bg-blue-600"}`}
                                    >
                                        <p className="text-sm font-bold uppercase">{p.type_periode[0] + p.numero}</p>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    <p className="text-xs font-medium xl:text-xs 3xl:text-sm">La période</p>
                    <div className="grid grid-cols-3 gap-2">
                        {periode.slice(0, 3).map((p, index) => (
                            <div
                                key={index}
                                onClick={() => handleClickPeriod(p)}
                                className={`px-2 py-1 text-xs rounded border cursor-pointer transition-colors duration-200 text-center
                                    ${selectedPeriod === p
                                        ? "bg-orange-500 text-white border-orange-400"
                                        : "bg-blue-600/60 text-white border-blue-400 hover:bg-blue-600"}`}
                            >
                                <p className="font-bold">{periods_label[index]}</p>
                            </div>
                        ))}
                    </div>
                    <div className="grid grid-cols-2 gap-2 mt-2">
                        {periode.slice(3, 5).map((p, index) => (
                            <div
                                key={index + 3}
                                onClick={() => handleClickPeriod(p)}
                                className={`px-2 py-1 text-xs rounded border cursor-pointer transition-colors duration-200 text-center
                                    ${selectedPeriod === p
                                        ? "bg-orange-500 text-white border-orange-400"
                                        : "bg-blue-600/60 text-white border-blue-400 hover:bg-blue-600"}`}
                            >
                                <p className="font-bold">{periods_label[index + 3]}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
            )}
        </>
    );
}

export default SideBarProf;
